import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
    CheckCircle, 
    Warning,
    Camera,
    FileText,
    ArrowRight,
    Spinner,
    Sun,
} from '@phosphor-icons/react';
import api from '../lib/api';

// Shared components/costanti del modulo sopralluogo
import { StatusBadge, StatusCallout, SURVEY_OUTCOMES } from './sopralluogo/SopralluogoShared';
import {
    PlanningSection,
    TechnicalSurveySection,
    ElectricalSurveySection,
    OutcomeSection,
} from './sopralluogo/SopralluogoFormSections';


// Main Sopralluogo Step Component
export default function SopralluogoStep({ 
    contractId, 
    nodeId, 
    stepCode = 'SOPRALLUOGO',
    onComplete,
    readOnly = false 
}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [transitioning, setTransitioning] = useState(false);
    const [runtimeState, setRuntimeState] = useState(null);
    const [formData, setFormData] = useState({});
    const [technicians, setTechnicians] = useState([]);
    const [config, setConfig] = useState(null);
    const [activeTab, setActiveTab] = useState('form');
    
    // Load runtime state
    const loadRuntimeState = useCallback(async () => {
        try {
            const [stateRes, configRes, techRes] = await Promise.all([
                api.get(`/step-runtime/state/${contractId}/${nodeId}?step_code=${stepCode}`),
                api.get(`/step-runtime/config/${stepCode}`),
                api.get('/technicians')
            ]);
            
            setRuntimeState(stateRes.data);
            setFormData(stateRes.data.form_data || {});
            setConfig(configRes.data);
            setTechnicians(techRes.data || []);
        } catch (error) {
            console.error('Error loading runtime state:', error);
            // Try to initialize if not found
            try {
                await api.post(`/step-runtime/initialize/${contractId}/${nodeId}?step_code=${stepCode}`);
                // Reload after initialization
                const stateRes = await api.get(`/step-runtime/state/${contractId}/${nodeId}?step_code=${stepCode}`);
                setRuntimeState(stateRes.data);
                setFormData(stateRes.data.form_data || {});
            } catch (initError) {
                console.error('Error initializing:', initError);
                toast.error('Errore nel caricamento dello step');
            }
        } finally {
            setLoading(false);
        }
    }, [contractId, nodeId, stepCode]);
    
    useEffect(() => {
        loadRuntimeState();
    }, [loadRuntimeState]);
    
    const handleFieldChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };
    
    const handleSave = async (showToast = true) => {
        setSaving(true);
        try {
            const result = await api.post(
                `/step-runtime/save/${contractId}/${nodeId}?step_code=${stepCode}`,
                { form_data: formData, auto_transition: true }
            );
            
            if (result.data.status_changed) {
                toast.success(`Stato aggiornato: ${result.data.internal_status}`);
            } else if (showToast) {
                toast.success('Dati salvati');
            }
            
            // Reload state
            await loadRuntimeState();
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };
    
    const handleTransition = async (toStatus) => {
        setTransitioning(true);
        try {
            // Save first
            await handleSave(false);
            
            // Execute transition
            const result = await api.post(
                `/step-runtime/transition/${contractId}/${nodeId}?step_code=${stepCode}`,
                { to_status: toStatus, form_data: formData }
            );
            
            toast.success(`Stato aggiornato: ${toStatus}`);
            
            if (result.data.workflow_advanced && onComplete) {
                onComplete(result.data);
            }
            
            // Reload state
            await loadRuntimeState();
        } catch (error) {
            console.error('Transition error:', error);
            const detail = error.response?.data?.detail;
            if (detail?.missing_fields) {
                toast.error(`Campi mancanti: ${detail.missing_fields.join(', ')}`);
            } else {
                toast.error(detail?.message || 'Errore nella transizione');
            }
        } finally {
            setTransitioning(false);
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Spinner className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }
    
    const internalStatus = runtimeState?.internal_status || 'DA_PIANIFICARE';
    const availableTransitions = runtimeState?.available_transitions || [];
    const documentsStatus = runtimeState?.documents_status || [];
    const isReadOnly = readOnly || internalStatus === 'COMPLETATO';
    
    // Get assigned technician name
    const assignedTech = technicians.find(t => t.id === formData.assigned_technician_id);
    
    return (
        <div className="space-y-4" data-testid="sopralluogo-step">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Sun weight="fill" className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <h3 className="font-semibold">Sopralluogo</h3>
                        <p className="text-xs text-muted-foreground">
                            {formData.planned_date && `Data: ${new Date(formData.planned_date).toLocaleDateString('it-IT')}`}
                            {assignedTech && ` • Tecnico: ${assignedTech.name}`}
                        </p>
                    </div>
                </div>
                <StatusBadge status={internalStatus} />
            </div>
            
            {/* Status Callout */}
            <StatusCallout 
                status={internalStatus} 
                missingFields={availableTransitions.find(t => !t.is_valid)?.missing_fields || []}
            />
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 w-full max-w-md">
                    <TabsTrigger value="form" className="text-xs">
                        <FileText className="h-4 w-4 mr-1" />
                        Dati
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="text-xs">
                        <Camera className="h-4 w-4 mr-1" />
                        Foto/Doc
                        {documentsStatus.some(d => d.mandatory && !d.is_satisfied) && (
                            <Badge variant="destructive" className="ml-1 h-4 w-4 p-0 text-[10px]">!</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="summary" className="text-xs">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Riepilogo
                    </TabsTrigger>
                </TabsList>
                
                <TabsContent value="form" className="mt-4">
                    <ScrollArea className="h-[60vh] pr-4">
                        {/* Planning Section */}
                        <PlanningSection
                            formData={formData}
                            handleFieldChange={handleFieldChange}
                            isReadOnly={isReadOnly}
                            internalStatus={internalStatus}
                            technicians={technicians}
                        />
                        
                        {/* Technical Survey Section */}
                        <TechnicalSurveySection
                            formData={formData}
                            handleFieldChange={handleFieldChange}
                            isReadOnly={isReadOnly}
                            internalStatus={internalStatus}
                        />
                        
                        {/* Electrical Survey Section */}
                        <ElectricalSurveySection
                            formData={formData}
                            handleFieldChange={handleFieldChange}
                            isReadOnly={isReadOnly}
                            internalStatus={internalStatus}
                        />
                        
                        {/* Outcome Section */}
                        <OutcomeSection
                            formData={formData}
                            handleFieldChange={handleFieldChange}
                            isReadOnly={isReadOnly}
                            internalStatus={internalStatus}
                        />
                    </ScrollArea>
                </TabsContent>
                
                <TabsContent value="documents" className="mt-4">
                    <Card>
                        <CardHeader className="py-3">
                            <CardTitle className="text-sm">Documenti e Foto Richiesti</CardTitle>
                            <CardDescription className="text-xs">
                                Carica le foto e i documenti del sopralluogo
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {documentsStatus.map(doc => (
                                    <div 
                                        key={doc.code}
                                        className={`flex items-center justify-between p-3 rounded-lg border ${
                                            doc.is_satisfied ? 'bg-green-50 border-green-200' : 
                                            doc.mandatory ? 'bg-red-50 border-red-200' : 'bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Camera className={`h-5 w-5 ${
                                                doc.is_satisfied ? 'text-green-600' : 
                                                doc.mandatory ? 'text-red-600' : 'text-gray-400'
                                            }`} />
                                            <div>
                                                <p className="text-sm font-medium">
                                                    {doc.label}
                                                    {doc.mandatory && <span className="text-red-500 ml-1">*</span>}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {doc.uploaded_count} file caricati
                                                    {doc.min_files > 0 && ` (min: ${doc.min_files})`}
                                                </p>
                                            </div>
                                        </div>
                                        {doc.is_satisfied ? (
                                            <CheckCircle weight="fill" className="h-5 w-5 text-green-600" />
                                        ) : (
                                            <Warning weight="fill" className="h-5 w-5 text-amber-500" />
                                        )}
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-4 text-center">
                                Usa la tab "Documenti" dello step per caricare i file
                            </p>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                <TabsContent value="summary" className="mt-4">
                    <Card>
                        <CardHeader className="py-3">
                            <CardTitle className="text-sm">Riepilogo Sopralluogo</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-xs text-muted-foreground">Data Pianificata</p>
                                        <p className="font-medium">
                                            {formData.planned_date 
                                                ? new Date(formData.planned_date).toLocaleDateString('it-IT')
                                                : '-'
                                            }
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Tecnico</p>
                                        <p className="font-medium">{assignedTech?.name || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Superficie</p>
                                        <p className="font-medium">
                                            {formData.available_surface_mq ? `${formData.available_surface_mq} mq` : '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Orientamento</p>
                                        <p className="font-medium capitalize">{formData.roof_orientation || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Esito</p>
                                        <p className={`font-medium ${
                                            formData.survey_outcome === 'POSITIVO' ? 'text-green-600' :
                                            formData.survey_outcome === 'NEGATIVO' ? 'text-red-600' :
                                            'text-amber-600'
                                        }`}>
                                            {SURVEY_OUTCOMES.find(o => o.value === formData.survey_outcome)?.label.split(' - ')[0] || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Potenza Consigliata</p>
                                        <p className="font-medium">
                                            {formData.recommended_power_kw ? `${formData.recommended_power_kw} kW` : '-'}
                                        </p>
                                    </div>
                                </div>
                                
                                {formData.survey_notes && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">Note</p>
                                        <p className="text-sm bg-muted p-2 rounded">{formData.survey_notes}</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            
            {/* Action Buttons */}
            {!isReadOnly && (
                <div className="flex items-center justify-between pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={() => handleSave(true)}
                        disabled={saving}
                    >
                        {saving ? <Spinner className="h-4 w-4 animate-spin mr-2" /> : null}
                        Salva Bozza
                    </Button>
                    
                    <div className="flex items-center gap-2">
                        {availableTransitions.map(transition => (
                            <Button
                                key={transition.action}
                                onClick={() => handleTransition(transition.to_status)}
                                disabled={transitioning || !transition.is_valid}
                                variant={transition.to_status === 'COMPLETATO' ? 'default' : 'secondary'}
                            >
                                {transitioning ? <Spinner className="h-4 w-4 animate-spin mr-2" /> : null}
                                {transition.label}
                                {transition.to_status === 'COMPLETATO' && (
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                )}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
